import React from 'react';
import { format } from 'date-fns';
import { Mail, Phone, Video, MessageSquare, FileText } from 'lucide-react';
import { InteractionLog, InteractionType } from '@/models/pilot-crm.model';

export interface ActivityFeedProps {
  activities: InteractionLog[];
  onActivityClick?: (activity: InteractionLog) => void;
  showLeadName?: boolean;
  leadNames?: Map<string, string>;
}

const INTERACTION_ICONS: Record<InteractionType, React.ReactNode> = {
  email_sent: <Mail className="w-4 h-4 text-blue-500" />,
  email_reply: <Mail className="w-4 h-4 text-green-500" />,
  call: <Phone className="w-4 h-4 text-purple-500" />,
  meeting: <Video className="w-4 h-4 text-orange-500" />,
  note: <MessageSquare className="w-4 h-4 text-gray-500" />,
  system: <FileText className="w-4 h-4 text-gray-400" />,
};

const INTERACTION_LABELS: Record<InteractionType, string> = {
  email_sent: 'Email Sent',
  email_reply: 'Email Reply',
  call: 'Call',
  meeting: 'Meeting',
  note: 'Note',
  system: 'System',
};

const INTERACTION_COLORS: Record<InteractionType, string> = {
  email_sent: 'bg-blue-50 border-blue-200',
  email_reply: 'bg-green-50 border-green-200',
  call: 'bg-purple-50 border-purple-200',
  meeting: 'bg-orange-50 border-orange-200',
  note: 'bg-gray-50 border-gray-200',
  system: 'bg-gray-50 border-gray-200',
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  onActivityClick,
  showLeadName = false,
  leadNames,
}) => {
  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <div
          key={activity.id}
          className={`p-3 rounded-lg border ${INTERACTION_COLORS[activity.type]} ${
            onActivityClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''
          }`}
          onClick={() => onActivityClick && onActivityClick(activity)}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{INTERACTION_ICONS[activity.type]}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900">
                  {INTERACTION_LABELS[activity.type]}
                </span>
                {showLeadName && leadNames && (
                  <span className="text-sm text-gray-500">
                    - {leadNames.get(activity.leadId) || 'Unknown'}
                  </span>
                )}
              </div>
              {activity.subject && (
                <p className="text-sm font-medium text-gray-700 mt-0.5 truncate">
                  {activity.subject}
                </p>
              )}
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{activity.summary}</p>
              <p className="text-xs text-gray-400 mt-2">
                {format(activity.createdAt, 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
